import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Select, Skeleton } from 'antd';
import { CameraOutlined, DownloadOutlined, InfoCircleOutlined, ProjectOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { projectService, Project, ProjectPhotoPost, PhotoType, PhotoFilters } from '../../services/projectService';
import { EmptyState, FilterChips, PersonAvatar, Pill } from '../ui';
import ImageLightbox, { LightboxImage } from '../ui/ImageLightbox';
import './ProjectPhotosDrawer.css';

type TypeFilter = 'all' | PhotoType;

interface PostedBy {
  userId: number;
  name: string;
  surname: string;
  imageUrl?: string;
  photos: number;
}

const photoCount = (posts: ProjectPhotoPost[]) => posts.reduce((sum, post) => sum + post.images.length, 0);

/** "villa-mueller-photos-before-arben-krasniqi.zip" — same idea as the names inside the ZIP. */
const slug = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .slice(0, 60);

/**
 * Projects → Photos: every photo employees posted on the project (feeds), filterable by employee
 * and before/after, and downloadable as one ZIP of exactly what the filters show.
 */
const ProjectPhotosDrawer: React.FC<{ project: Project | null; onClose: () => void }> = ({ project, onClose }) => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState<ProjectPhotoPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState<number | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [downloading, setDownloading] = useState(false);
  const [viewer, setViewer] = useState<{ images: LightboxImage[]; index: number } | null>(null);

  const projectId = project?.id;

  const load = async (id: number) => {
    setLoading(true);
    try {
      setPosts(await projectService.getFeedImages(id));
    } catch {
      setPosts([]);
      toast.error(t('projects.photos.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    setEmployeeFilter('all');
    setTypeFilter('all');
    setPosts([]);
    load(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Everyone who posted photos here, for the employee filter
  const postedBy = useMemo(() => {
    const map = new Map<number, PostedBy>();
    posts.forEach((post) => {
      const existing = map.get(post.user_id);
      if (existing) {
        existing.photos += post.images.length;
        return;
      }
      const employee = post.user?.employee;
      const [first, ...rest] = (post.user?.name ?? '').split(' ');
      map.set(post.user_id, {
        userId: post.user_id,
        name: employee?.name ?? first,
        surname: employee?.surname ?? rest.join(' '),
        imageUrl: employee?.thumbnailUrl || employee?.imageUrl,
        photos: post.images.length,
      });
    });
    return Array.from(map.values()).sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));
  }, [posts]);

  const byEmployee = useMemo(
    () => (employeeFilter === 'all' ? posts : posts.filter((post) => post.user_id === employeeFilter)),
    [posts, employeeFilter],
  );

  const filtered = useMemo(
    () => (typeFilter === 'all' ? byEmployee : byEmployee.filter((post) => post.type === typeFilter)),
    [byEmployee, typeFilter],
  );

  const filteredPhotos = photoCount(filtered);
  const hasFilters = employeeFilter !== 'all' || typeFilter !== 'all';

  // One flat list for the viewer, so ←/→ walks through everything the filters show
  const { lightboxImages, firstIndex } = useMemo(() => {
    const images: LightboxImage[] = [];
    const first = new Map<number, number>();
    filtered.forEach((post) => {
      first.set(post.id, images.length);
      post.images.forEach((image) => images.push({ full: image.imageUrl, thumb: image.thumbnailUrl }));
    });
    return { lightboxImages: images, firstIndex: first };
  }, [filtered]);

  // Grouped under Today / Yesterday / date, newest first (the API's order)
  const groups = useMemo(() => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const result: { key: string; label: string; posts: ProjectPhotoPost[] }[] = [];
    filtered.forEach((post) => {
      const date = new Date(post.created_at);
      const key = date.toDateString();
      const group = result.find((entry) => entry.key === key);
      if (group) {
        group.posts.push(post);
        return;
      }
      result.push({
        key,
        label:
          key === today
            ? t('feeds.today')
            : key === yesterday
              ? t('feeds.yesterday')
              : date.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
        posts: [post],
      });
    });
    return result;
  }, [filtered, t, i18n.language]);

  const typeLabel = (type: PhotoType) => (type === 'after' ? t('dashboard.feedAfter') : t('dashboard.feedBefore'));

  const handleDownload = async () => {
    if (!project || filteredPhotos === 0) return;
    const filters: PhotoFilters = {
      ...(employeeFilter !== 'all' ? { user_id: employeeFilter } : {}),
      ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
    };
    const person = postedBy.find((entry) => entry.userId === employeeFilter);
    const fileName =
      [slug(project.name) || `project-${project.id}`, 'photos', filters.type, person ? slug(`${person.name} ${person.surname}`) : null]
        .filter(Boolean)
        .join('-') + '.zip';

    setDownloading(true);
    try {
      const blob = await projectService.downloadFeedImagesZip(project.id, filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error: any) {
      toast.error(error?.message && !error?.isAxiosError ? error.message : t('projects.photos.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const typeOptions = [
    { key: 'all' as TypeFilter, label: t('projects.photos.all'), count: photoCount(byEmployee) },
    {
      key: 'before' as TypeFilter,
      label: t('dashboard.feedBefore'),
      count: photoCount(byEmployee.filter((post) => post.type === 'before')),
    },
    {
      key: 'after' as TypeFilter,
      label: t('dashboard.feedAfter'),
      count: photoCount(byEmployee.filter((post) => post.type === 'after')),
    },
  ];

  return (
    <Drawer
      title={t('projects.photos.title')}
      placement="right"
      onClose={onClose}
      open={!!project}
      width={760}
      // Esc belongs to the photo viewer while it's open
      keyboard={!viewer}
      extra={
        project ? (
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={() => load(project.id)}
            disabled={loading}
            aria-label={t('dashboard.refresh')}
            title={t('dashboard.refresh')}
          />
        ) : null
      }
    >
      {project && (
        <>
          <div className="prj-drawer-subject">
            <ProjectOutlined />
            <div className="e22-row-body">
              <div className="e22-row-title">{project.name}</div>
              <div className="e22-row-subtitle">{project.location}</div>
            </div>
          </div>

          {/* Filters + download first; they stay on top while the photos scroll */}
          <div className="pph-toolbar">
            <div className="pph-filters">
              <Select
                className="pph-employee"
                value={employeeFilter}
                onChange={setEmployeeFilter}
                disabled={loading || postedBy.length === 0}
                showSearch
                optionFilterProp="label"
                aria-label={t('projects.photos.employee')}
                options={[
                  { value: 'all', label: t('projects.photos.allEmployees') },
                  ...postedBy.map((person) => ({
                    value: person.userId,
                    label: `${person.name} ${person.surname}`.trim(),
                  })),
                ]}
                optionRender={(option) => {
                  const person = postedBy.find((entry) => entry.userId === option.value);
                  return (
                    <div className="pph-option">
                      {person ? (
                        <PersonAvatar name={person.name} surname={person.surname} imageUrl={person.imageUrl} size={22} />
                      ) : null}
                      <span className="pph-option-label">{option.label}</span>
                      {person ? <span className="pph-option-count">{person.photos}</span> : null}
                    </div>
                  );
                }}
              />
              <FilterChips
                options={typeOptions}
                value={typeFilter}
                onChange={setTypeFilter}
                ariaLabel={t('projects.photos.title')}
              />
            </div>

            <Button
              type="primary"
              block
              size="large"
              icon={<DownloadOutlined />}
              loading={downloading}
              disabled={loading || filteredPhotos === 0}
              onClick={handleDownload}
            >
              {downloading
                ? t('projects.photos.preparing')
                : t('projects.photos.download', { count: filteredPhotos })}
            </Button>
            <div className="pph-hint">
              <InfoCircleOutlined aria-hidden />
              <span>{t('projects.photos.zipHint')}</span>
            </div>
          </div>

          {loading ? (
            <div className="pph-skeleton">
              <Skeleton active avatar paragraph={{ rows: 1 }} />
              <div className="pph-grid">
                {Array.from({ length: 8 }).map((_, index) => (
                  <Skeleton.Image key={index} active className="pph-skeleton-tile" />
                ))}
              </div>
            </div>
          ) : posts.length === 0 ? (
            <EmptyState icon={<CameraOutlined />} text={t('projects.photos.noPhotos')} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<CameraOutlined />}
              text={t('projects.photos.noMatch')}
              action={
                <button
                  type="button"
                  className="e22-link-button"
                  onClick={() => {
                    setEmployeeFilter('all');
                    setTypeFilter('all');
                  }}
                >
                  {t('projects.clearFilters')}
                </button>
              }
            />
          ) : (
            <>
              <div className="pph-summary">
                <span>
                  {t('projects.photos.photoCount', { count: filteredPhotos })} ·{' '}
                  {t('projects.photos.postCount', { count: filtered.length })}
                </span>
                {hasFilters ? (
                  <button
                    type="button"
                    className="e22-link-button"
                    onClick={() => {
                      setEmployeeFilter('all');
                      setTypeFilter('all');
                    }}
                  >
                    {t('projects.clearFilters')}
                  </button>
                ) : null}
              </div>

              {groups.map((group) => (
                <section key={group.key} className="pph-day">
                  <h3 className="pph-day-label">{group.label}</h3>
                  {group.posts.map((post) => {
                    const employee = post.user?.employee;
                    const [first, ...rest] = (post.user?.name ?? '').split(' ');
                    const start = firstIndex.get(post.id) ?? 0;
                    return (
                      <article key={post.id} className="pph-post">
                        <header className="pph-post-head">
                          <PersonAvatar
                            name={employee?.name ?? first}
                            surname={employee?.surname ?? rest.join(' ')}
                            imageUrl={employee?.thumbnailUrl || employee?.imageUrl}
                            size={32}
                          />
                          <div className="e22-row-body">
                            <div className="e22-row-title">{post.user?.name}</div>
                            <div className="e22-row-subtitle">
                              {new Date(post.created_at).toLocaleTimeString(i18n.language, {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}{' '}
                              · {t('projects.photos.photoCount', { count: post.images.length })}
                            </div>
                          </div>
                          <Pill tone={post.type === 'after' ? 'success' : 'primary'}>{typeLabel(post.type)}</Pill>
                        </header>
                        {post.description ? <p className="pph-post-text">{post.description}</p> : null}
                        <div className="pph-grid">
                          {post.images.map((image, index) => (
                            <button
                              key={image.id}
                              type="button"
                              className="e22-thumb pph-tile"
                              onClick={() => setViewer({ images: lightboxImages, index: start + index })}
                              aria-label={t('feeds.openPhoto', { number: index + 1 })}
                            >
                              <img src={image.thumbnailUrl || image.imageUrl} alt="" loading="lazy" decoding="async" />
                            </button>
                          ))}
                        </div>
                      </article>
                    );
                  })}
                </section>
              ))}
            </>
          )}

          {viewer && (
            <ImageLightbox
              images={viewer.images}
              startIndex={viewer.index}
              downloadName={`${project.name} photos`}
              onClose={() => setViewer(null)}
              labels={{
                close: t('common.close'),
                previous: t('feeds.previousPhoto'),
                next: t('feeds.nextPhoto'),
                openOriginal: t('feeds.openOriginal'),
                download: t('feeds.downloadPhoto'),
              }}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default ProjectPhotosDrawer;
